import React from 'react';
import { Plus, Filter, Sparkles, Trophy, Calendar, MapPin } from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';

interface PickupGamesEmptyIllustrationProps {
  title?: string;
  description?: string;
  selectedSport?: string;
  onResetFilters?: () => void;
  onOpenHostGame?: () => void;
}

export const PickupGamesEmptyIllustration: React.FC<PickupGamesEmptyIllustrationProps> = ({
  title = "No Scheduled Pickup Runs Found",
  description,
  selectedSport = "all",
  onResetFilters,
  onOpenHostGame,
}) => {
  const dynamicDescription = description || 
    (selectedSport !== 'all'
      ? `There are currently no active pickup games matching "${selectedSport.toUpperCase()}". Adjust your sport filter or be the first to host a game on your local playground court!`
      : "No scheduled pickup runs found matching your search or filters. Host a new game to rally local ballers and earn +50 XP!");

  return (
    <div 
      id="pickup-games-empty-illustration-container"
      className="relative w-full max-w-xl mx-auto p-8 sm:p-10 text-center bg-gradient-to-b from-indigo-950/90 via-indigo-900/60 to-indigo-950/90 rounded-[2.5rem] border border-white/15 shadow-2xl overflow-hidden space-y-6 group"
    >
      {/* Ambient Backdrop Glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-lime-400/10 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-60 h-60 bg-lime-400/15 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute -bottom-24 -right-24 w-60 h-60 bg-amber-400/15 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* CUSTOM SVG VECTOR ILLUSTRATION FOR PICKUP GAMES */}
      <div className="relative w-48 h-48 mx-auto flex items-center justify-center">
        {/* Outer Glowing Energy Rings */}
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-lime-400/30 animate-[spin_25s_linear_infinite]" />
        <div className="absolute inset-3 rounded-full border border-white/10 bg-indigo-950/80 backdrop-blur-md shadow-2xl flex items-center justify-center" />

        {/* Playground Court & Basketball Rim Vector Artwork */}
        <svg
          viewBox="0 0 200 200"
          className="w-full h-full relative z-10 drop-shadow-[0_10px_25px_rgba(163,230,53,0.3)]"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Playground Court Surface Background */}
          <rect x="25" y="45" width="150" height="110" rx="16" fill="#090d26" stroke="#ffffff" strokeOpacity="0.12" strokeWidth="2" />
          
          {/* Half-Court Lines & Key Area */}
          <line x1="25" y1="100" x2="175" y2="100" stroke="#ffffff" strokeOpacity="0.15" strokeWidth="2" />
          <circle cx="100" cy="100" r="22" stroke="#a3e635" strokeOpacity="0.4" strokeWidth="2" strokeDasharray="3 3" />
          
          {/* Three Point Arc */}
          <path d="M 60 45 C 60 80 140 80 140 45" stroke="#38bdf8" strokeOpacity="0.4" strokeWidth="2" strokeLinecap="round" />

          {/* Basketball Backboard (Center Top) */}
          <rect x="75" y="55" width="50" height="32" rx="4" fill="#1e1b4b" stroke="#ffffff" strokeWidth="2" />
          <rect x="87" y="65" width="26" height="18" rx="2" fill="none" stroke="#f43f5e" strokeWidth="1.5" />
          
          {/* Rim & Net */}
          <ellipse cx="100" cy="84" rx="14" ry="4.5" fill="#f43f5e" fillOpacity="0.3" stroke="#f43f5e" strokeWidth="2" />
          <path d="M 88 85 L 93 102 L 100 85 L 107 102 L 112 85" stroke="#ffffff" strokeOpacity="0.6" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M 93 102 L 100 110 L 107 102" stroke="#ffffff" strokeOpacity="0.6" strokeWidth="1.5" strokeLinecap="round" />

          {/* Glowing Ball in Flight with Trajectory */}
          <path d="M 45 135 Q 70 85 96 85" stroke="#a3e635" strokeWidth="2.5" strokeDasharray="4 4" strokeLinecap="round" opacity="0.8" />
          
          {/* Basketball */}
          <g transform="translate(42, 130)" filter="drop-shadow(0 4px 8px rgba(0,0,0,0.5))">
            <circle cx="12" cy="12" r="11" fill="#f97316" stroke="#ffffff" strokeWidth="1.5" />
            <path d="M 12 1 V 23 M 1 12 H 23" stroke="#000000" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M 4 5 C 9 8 9 16 4 19" stroke="#000000" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M 20 5 C 15 8 15 16 20 19" stroke="#000000" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
          </g>

          {/* Sparkles & Star Accents */}
          <circle cx="160" cy="65" r="3" fill="#a3e635" className="animate-pulse" />
          <path d="M 160 58 L 161.5 62.5 L 166 64 L 161.5 65.5 L 160 70 L 158.5 65.5 L 154 64 L 158.5 62.5 Z" fill="#a3e635" />

          <circle cx="35" cy="65" r="2.5" fill="#facc15" />
          <path d="M 35 60 L 36 63.5 L 39.5 64.5 L 36 65.5 L 35 69 L 34 65.5 L 30.5 64.5 L 34 63.5 Z" fill="#facc15" />
        </svg>
      </div>

      {/* Header & Description */}
      <div className="space-y-2">
        <h3 className="text-xl sm:text-2xl font-black italic uppercase text-white tracking-tight">
          {title}
        </h3>
        <p className="text-xs text-indigo-200/80 leading-relaxed max-w-md mx-auto font-medium">
          {dynamicDescription}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
        {onResetFilters && (
          <button
            type="button"
            id="btn-reset-game-filters-illustration"
            onClick={() => {
              triggerHaptic('medium');
              onResetFilters();
            }}
            className="px-5 py-3 bg-indigo-950 hover:bg-indigo-800 text-lime-300 border border-lime-400/40 text-xs font-black italic uppercase rounded-2xl transition flex items-center space-x-2 cursor-pointer shadow-md"
          >
            <Filter className="w-4 h-4 text-lime-400" />
            <span>Reset Game Filters</span>
          </button>
        )}

        {onOpenHostGame && (
          <button
            type="button"
            id="btn-host-pickup-run-illustration"
            onClick={() => {
              triggerHaptic('heavy');
              onOpenHostGame();
            }}
            className="px-6 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-lg shadow-lime-400/25 transition flex items-center space-x-2 cursor-pointer"
          >
            <Plus className="w-4 h-4 stroke-[2.5]" />
            <span>Host a Pickup Run (+50 XP)</span>
          </button>
        )}
      </div>
    </div>
  );
};
